import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { useDeleteAllPickemsMutation } from '../../../api/dev-data/deleteAllPickems';
import { useFlushCacheMutation } from '../../../api/dev-data/flushCache';
import { useRegenerateAggregateLeaderboards } from '../../../api/leaderboard/regenerateAggregateLeaderboards';
import { env } from '../../../config/env';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Shield } from 'lucide-react';

export const Route = createFileRoute('/dashboard/dev-tools/')({
  component: DevToolsPage,
});

interface ProgressMessage {
  message: string;
  timestamp: Date;
  type?: 'info' | 'success' | 'error';
  success?: boolean;
  duration?: string;
  stats?: {
    totalUsers: number;
    pickemsCreated: number;
    questionsPerPickem: number;
    avgUsersPerPickem: number;
  };
  error?: string;
  details?: string;
}

function DevToolsPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressMessages, setProgressMessages] = useState<ProgressMessage[]>([]);
  const [generationStatus, setGenerationStatus] = useState<
    'idle' | 'generating' | 'success' | 'error'
  >('idle');
  const scrollRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const deleteMutation = useDeleteAllPickemsMutation();
  const flushCacheMutation = useFlushCacheMutation();
  const regenerateMutation = useRegenerateAggregateLeaderboards();
  const isProduction = env.NODE_ENV === 'production';

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [progressMessages]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  if (!env.ENABLE_DEV_TOOLS) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>Dev Tools are not enabled for this environment.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show warning gate if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-500 dark:border-red-600">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Shield className="h-16 w-16 text-red-600 dark:text-red-500" />
            </div>
            <CardTitle className="text-2xl text-red-700 dark:text-red-400">
              ⚠️ Here Be Dragons!
            </CardTitle>
            <CardDescription className="text-base mt-4 text-gray-700 dark:text-gray-300 font-medium">
              You probably don't need to use these tools. They have the potential to remove all
              data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-amber-300 bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40">
              <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />
              <AlertTitle className="text-amber-900 dark:text-amber-200 font-semibold">
                Warning
              </AlertTitle>
              <AlertDescription className="text-amber-800 dark:text-amber-300 font-medium">
                The Dev Tools can permanently delete data and should only be used by developers who
                understand the consequences.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label
                htmlFor="password"
                className="text-sm font-semibold text-gray-700 dark:text-gray-200"
              >
                Type the word{' '}
                <span className="font-mono text-red-600 dark:text-red-400 font-bold">"danger"</span>{' '}
                to proceed:
              </Label>
              <Input
                id="password"
                type="text"
                value={passwordInput}
                onChange={e => setPasswordInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && passwordInput.toLowerCase() === 'danger') {
                    setIsAuthenticated(true);
                  }
                }}
                placeholder="Enter the magic word..."
                className="font-mono"
              />
              <Button
                onClick={() => {
                  if (passwordInput.toLowerCase() === 'danger') {
                    setIsAuthenticated(true);
                  }
                }}
                disabled={passwordInput.toLowerCase() !== 'danger'}
                className="w-full"
                variant={passwordInput.toLowerCase() === 'danger' ? 'destructive' : 'default'}
              >
                {passwordInput.toLowerCase() === 'danger' ? 'Enter Dev Tools' : 'Access Denied'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleGenerate = () => {
    setIsGenerating(true);
    setGenerationStatus('generating');
    setProgressMessages([]);

    const token = localStorage.getItem('access_token');
    const eventSource = new EventSource(
      `${env.API_BASE_URL}/api/admin/dev-data/generate-pickems?token=${token}`
    );
    eventSourceRef.current = eventSource;

    eventSource.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        const newMessage: ProgressMessage = {
          message: data.message,
          timestamp: new Date(),
          type: data.error ? 'error' : data.success ? 'success' : 'info',
          success: data.success,
          duration: data.duration,
          stats: data.stats,
          error: data.error,
          details: data.details,
        };

        setProgressMessages(prev => [...prev, newMessage]);

        if (data.error) {
          setGenerationStatus('error');
          setIsGenerating(false);
          eventSource.close();
        } else if (data.success) {
          setGenerationStatus('success');
          setIsGenerating(false);
          eventSource.close();
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    eventSource.onerror = error => {
      console.error('SSE Error:', error);
      setProgressMessages(prev => [
        ...prev,
        {
          message: 'Connection error occurred',
          timestamp: new Date(),
          type: 'error',
          error: 'Failed to connect to server',
        },
      ]);
      setGenerationStatus('error');
      setIsGenerating(false);
      eventSource.close();
    };
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const handleFlushCache = () => {
    flushCacheMutation.mutate();
  };

  const handleRegenerateLeaderboards = () => {
    regenerateMutation.mutate();
  };

  const handleClearLog = () => {
    setProgressMessages([]);
    setGenerationStatus('idle');
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const getMessageColor = (type?: string) => {
    switch (type) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-slate-600 dark:text-slate-300';
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Dev Tools</h1>
        <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <AlertDescription className="text-yellow-800 dark:text-yellow-200">
            {isProduction
              ? '⚠️ Production Mode - Only safe operations (cache management, leaderboard regeneration) are available'
              : '⚠️ Development Only - All dev tools are available'}
          </AlertDescription>
        </Alert>
      </div>

      <div className="space-y-6">
        {/* Generate Dummy Data Card - Hide in production */}
        {!isProduction && (
          <Card>
            <CardHeader>
              <CardTitle>Dummy Data Generator</CardTitle>
              <CardDescription>
                Generate test pickems with questions and user selections for development and testing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>This will create:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>120 Test users</li>
                  <li>35 Total pickems:</li>
                  <ul className="list-circle list-inside ml-6 space-y-0.5">
                    <li>4 Active pickems (future dates: today + 1, 2, 3, 4 days at 7pm)</li>
                    <li>1 Active pickem (today at 1am)</li>
                    <li>30 Completed pickems (past dates with answers marked and scored)</li>
                  </ul>
                  <li>
                    Each pickem includes 6 questions: Team, Player, Prop, Over/Under, Player
                    (assists), and Tiebreaker
                  </li>
                  <li>~72 user selections per completed pickem (60% participation rate)</li>
                </ul>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="w-full sm:w-auto"
                >
                  {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isGenerating ? 'Generating...' : 'Generate Test Data'}
                </Button>
                {progressMessages.length > 0 && (
                  <Button variant="outline" onClick={handleClearLog} disabled={isGenerating}>
                    Clear Log
                  </Button>
                )}
              </div>

              {/* Progress Log */}
              {progressMessages.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      Progress Log
                      {generationStatus === 'generating' && (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      )}
                      {generationStatus === 'success' && (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      )}
                      {generationStatus === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                    </h3>
                  </div>
                  <div className="border rounded-lg bg-slate-950 dark:bg-slate-900">
                    <ScrollArea className="h-80">
                      <div ref={scrollRef} className="p-4 space-y-1 font-mono text-xs">
                        {progressMessages.map((msg, index) => (
                          <div key={index} className="flex gap-3">
                            <span className="text-slate-500 select-none">
                              [{formatTimestamp(msg.timestamp)}]
                            </span>
                            <span className={getMessageColor(msg.type)}>
                              {msg.message}
                              {msg.stats && (
                                <div className="mt-1 ml-2 space-y-0.5">
                                  <div>Duration: {msg.duration}s</div>
                                  <div>Total Users: {msg.stats.totalUsers}</div>
                                  <div>Pickems Created: {msg.stats.pickemsCreated}</div>
                                  <div>Questions per Pickem: {msg.stats.questionsPerPickem}</div>
                                  <div>Avg Users per Pickem: {msg.stats.avgUsersPerPickem}</div>
                                </div>
                              )}
                              {msg.error && msg.details && (
                                <div className="mt-1 ml-2 text-red-400">Details: {msg.details}</div>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Flush Cache Card */}
        <Card className="border-yellow-200 dark:border-yellow-900">
          <CardHeader>
            <CardTitle className="text-yellow-600 dark:text-yellow-400">Cache Management</CardTitle>
            <CardDescription>Clear all Redis cache keys</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
              <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                ⚠️ This will flush all Redis cache keys. Cached data will be regenerated on next
                request.
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              onClick={handleFlushCache}
              disabled={flushCacheMutation.isPending}
              className="w-full sm:w-auto border-yellow-500 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-950"
            >
              {flushCacheMutation.isPending ? 'Flushing...' : 'Flush Redis Cache'}
            </Button>
          </CardContent>
        </Card>

        {/* Regenerate Aggregate Leaderboards Card */}
        <Card className="border-blue-200 dark:border-blue-900">
          <CardHeader>
            <CardTitle className="text-blue-600 dark:text-blue-400">
              Leaderboard Regeneration
            </CardTitle>
            <CardDescription>Recalculate all aggregate leaderboards from scratch</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                ⚠️ This will delete and regenerate all aggregate leaderboard data for all leagues
                and periods (today, week, month, year). Use this after making changes to the
                leaderboard calculation logic or if leaderboard data becomes inconsistent.
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              onClick={handleRegenerateLeaderboards}
              disabled={regenerateMutation.isPending}
              className="w-full sm:w-auto border-blue-500 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
            >
              {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate All Leaderboards'}
            </Button>
          </CardContent>
        </Card>

        {/* Delete All Pickems Card - Hide in production */}
        {!isProduction && (
          <Card className="border-red-200 dark:border-red-900">
            <CardHeader>
              <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
              <CardDescription>Permanently delete all pickems and related data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  ⚠️ This will permanently delete all pickems, questions, options, and user
                  selections. This action cannot be undone.
                </AlertDescription>
              </Alert>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleteMutation.isPending}
                className="w-full sm:w-auto"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Remove All Pickems'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete all pickems, questions,
              options, user selections, and leaderboard data from the database.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete All Pickems
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
